import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  AmountCell,
  FinancialStatementStyles,
  NodeNameCell,
  PresetChips,
  amount,
  expandableKeys,
  isNodeRow,
  nodeKey,
  periodPresets,
  previousPeriod,
  printStatement,
  pushNodeRows,
  rowAmounts,
  samePeriodLastYear,
} from "./FinancialStatementShared";

const TITLE = "Profit & Loss Account Statement";

// Statement layout: account lines between the running profit subtotals.
const LAYOUT = [
  { line: "revenue" },
  { line: "direct_operating_expenses" },
  { subtotal: "gross_profit", label: "Gross / Operating Profit" },
  { line: "administration_expenses" },
  { line: "salary_remuneration" },
  { line: "selling_marketing_expenses" },
  { subtotal: "operating_profit", label: "Operating Profit (EBIT)" },
  { line: "non_operating_income" },
  { line: "unclassified", onlyWhenUsed: true },
  {
    subtotal: "pbit",
    label: "Profit Before Interest and Tax (PBIT)",
  },
  { line: "finance_costs" },
  { subtotal: "profit_before_tax", label: "Profit Before Tax (PBT)" },
  { line: "current_tax_expense" },
  { line: "deferred_tax_expense" },
  {
    subtotal: "net_profit_after_tax",
    label: "Net Profit After Tax (NPAT)",
    grand: true,
  },
];

const buildRows = (data, expanded, hideZero) => {
  const rows = [];
  LAYOUT.forEach((item) => {
    if (item.line) {
      const line = data.lines[item.line];
      if (!line || (item.onlyWhenUsed && !line.children.length)) {
        return;
      }
      pushNodeRows(rows, [line], 1, expanded, hideZero);
      return;
    }
    rows.push({
      type: item.grand ? "grand" : "subtotal",
      key: `s-${item.subtotal}`,
      label: item.label,
      amount: data.totals[item.subtotal],
      compare_amount: data.compare_totals
        ? data.compare_totals[item.subtotal]
        : null,
    });
  });
  return rows;
};

const percentOf = (value, base) => {
  const b = num(base);
  if (Math.abs(b) < 0.005) return "";
  return `${((num(value) / b) * 100).toFixed(1)}%`;
};

const lineNote = (node, data) => {
  if (!node.key) return null;
  if (node.key === "revenue" && data.revenue_source === "orders") {
    return "VAT-able value of sales orders in the period";
  }
  if (node.key === "unclassified") {
    return "Income and expense accounts not mapped to any line";
  }
  if (!node.mapped) {
    return "No account mapped to this line";
  }
  return null;
};

const ProfitAndLoss = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [, thisMonthFrom, thisMonthTo] = periodPresets()[0];
  const [inputs, setInputs] = useState({
    from: searchParams.get("from") || thisMonthFrom,
    to: searchParams.get("to") || thisMonthTo,
    compare_from: searchParams.get("compare_from") || "",
    compare_to: searchParams.get("compare_to") || "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [hideZero, setHideZero] = useState(false);

  const fetchData = async (params = inputs) => {
    setLoading(true);
    setAlert(null);

    const query = { from: params.from, to: params.to };
    if (params.compare_from && params.compare_to) {
      query.compare_from = params.compare_from;
      query.compare_to = params.compare_to;
    }
    setSearchParams(query, { replace: true });

    try {
      const res = await Http.get("/profit-and-loss", { params: query });
      setData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the statement. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const apply = (changes) => {
    const next = { ...inputs, ...changes };
    setInputs(next);
    fetchData(next);
  };

  const hasCompare = !!(data && data.compare_from);

  const rows = useMemo(
    () => (data ? buildRows(data, expanded, hideZero) : []),
    [data, expanded, hideZero],
  );

  const allKeys = useMemo(
    () => (data ? expandableKeys(Object.values(data.lines)) : []),
    [data],
  );

  const allExpanded =
    allKeys.length > 0 && allKeys.every((key) => expanded[key]);

  const toggleAll = () =>
    setExpanded(
      allExpanded
        ? {}
        : allKeys.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    );

  const toggle = (node) => {
    const key = nodeKey(node);
    setExpanded((values) => ({ ...values, [key]: !values[key] }));
  };

  const revenue = data ? data.lines.revenue.amount : 0;
  const compareRevenue = hasCompare ? data.lines.revenue.compare_amount : 0;
  const unclassified = data ? data.lines.unclassified : null;

  const periodLabel = (from, to) => `${formatDate(from)} – ${formatDate(to)}`;

  const renderRow = (row) => {
    const { amount: value, compare } = rowAmounts(row);
    const isNode = isNodeRow(row);

    return (
      <tr key={row.key} className={`fs-${row.type}`}>
        {isNode ? (
          <NodeNameCell
            row={row}
            onToggle={toggle}
            note={lineNote(row.node, data)}
          />
        ) : (
          <td>{row.label}</td>
        )}
        <td className="fs-code">{isNode ? row.node.code : ""}</td>
        <AmountCell value={value} />
        <td className="coa-num fs-pct">{percentOf(value, revenue)}</td>
        {hasCompare && <AmountCell value={compare} />}
        {hasCompare && (
          <td className="coa-num fs-pct">{percentOf(compare, compareRevenue)}</td>
        )}
        {hasCompare && <AmountCell value={num(value) - num(compare)} />}
      </tr>
    );
  };

  const handlePrint = () => {
    const headHtml = `
      <th>Particulars</th>
      <th>Code</th>
      <th class="num">${periodLabel(data.from, data.to)}</th>
      <th class="num">% of Revenue</th>
      ${
        hasCompare
          ? `<th class="num">${periodLabel(data.compare_from, data.compare_to)}</th><th class="num">% of Revenue</th><th class="num">Change</th>`
          : ""
      }`;

    const bodyHtml = rows
      .map((row) => {
        const { amount: value, compare } = rowAmounts(row);
        const isNode = isNodeRow(row);
        const pad = isNode ? 16 + row.depth * 16 : 8;
        return `
          <tr class="${row.type}">
            <td style="padding-left:${pad}px">${isNode ? row.node.name : row.label}</td>
            <td class="code">${isNode && row.node.code ? row.node.code : ""}</td>
            <td class="num">${amount(value)}</td>
            <td class="num">${percentOf(value, revenue)}</td>
            ${
              hasCompare
                ? `<td class="num">${amount(compare)}</td><td class="num">${percentOf(compare, compareRevenue)}</td><td class="num">${amount(num(value) - num(compare))}</td>`
                : ""
            }
          </tr>`;
      })
      .join("");

    printStatement({
      title: TITLE,
      subtitle: `For the period ${periodLabel(data.from, data.to)}${
        hasCompare
          ? ` (compared with ${periodLabel(data.compare_from, data.compare_to)})`
          : ""
      } &middot; Amounts in Taka`,
      headHtml,
      bodyHtml,
      note:
        data.revenue_source === "orders"
          ? "Revenue is the VAT-able value of sales orders in the period."
          : "",
    });
  };

  const stat = (label, icon, value, compare, withMargin) => (
    <div className="col-md-3 mg-b-10">
      <div
        className={`coa-stat ${withMargin ? (num(value) < 0 ? "is-alert" : "is-ok") : ""}`}
      >
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{amount(value)}</span>
        <span className="coa-stat-sub">
          {withMargin && percentOf(value, revenue)
            ? `${percentOf(value, revenue)} of revenue`
            : periodLabel(data.from, data.to)}
          {hasCompare && ` · ${amount(compare)} before`}
        </span>
      </div>
    </div>
  );

  const periodIsActive = ([, from, to]) =>
    inputs.from === from && inputs.to === to;

  const compareOptions =
    inputs.from && inputs.to
      ? [
          ["Previous period", ...previousPeriod(inputs.from, inputs.to)],
          ["Same period last year", ...samePeriodLastYear(inputs.from, inputs.to)],
          ...(inputs.compare_from ? [["No comparison", "", ""]] : []),
        ]
      : [];

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">{TITLE}</h5>
            <p className="text-muted mb-0 tx-12">
              For the period{" "}
              {periodLabel(data?.from || inputs.from, data?.to || inputs.to)}
              {hasCompare &&
                ` compared with ${periodLabel(data.compare_from, data.compare_to)}`}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/reports" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Reports
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !data}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-calendar-o me-2"></i>Statement Period
              </span>
              <span className="coa-head-note">
                Postings dated inside the period, both days included
              </span>
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-5">
                  <label>Period</label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control"
                      name="from"
                      value={inputs.from}
                      onChange={handleChange}
                      max={inputs.to}
                      aria-label="From"
                    />
                    <input
                      type="date"
                      className="form-control"
                      name="to"
                      value={inputs.to}
                      onChange={handleChange}
                      min={inputs.from}
                      aria-label="To"
                    />
                  </div>
                  <PresetChips
                    presets={periodPresets()}
                    isActive={periodIsActive}
                    onPick={([, from, to]) => apply({ from, to })}
                  />
                </div>
                <div className="col-lg-5 mg-t-10 mg-lg-t-0">
                  <label>Compare with (optional)</label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control"
                      name="compare_from"
                      value={inputs.compare_from}
                      onChange={handleChange}
                      max={inputs.compare_to || undefined}
                      aria-label="Compare from"
                    />
                    <input
                      type="date"
                      className="form-control"
                      name="compare_to"
                      value={inputs.compare_to}
                      onChange={handleChange}
                      min={inputs.compare_from || undefined}
                      aria-label="Compare to"
                    />
                  </div>
                  <PresetChips
                    presets={compareOptions}
                    isActive={([label, from, to]) =>
                      label !== "No comparison" &&
                      inputs.compare_from === from &&
                      inputs.compare_to === to
                    }
                    onPick={([, from, to]) =>
                      apply({ compare_from: from, compare_to: to })
                    }
                  />
                </div>
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchData()}
                    disabled={loading || !inputs.from || !inputs.to}
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-search"} mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {data && (
            <div className="row mg-b-20">
              {stat(
                "Revenue",
                "fa-line-chart",
                revenue,
                compareRevenue,
                false,
              )}
              {stat(
                "Gross Profit",
                "fa-cubes",
                data.totals.gross_profit,
                data.compare_totals?.gross_profit,
                true,
              )}
              {stat(
                "Operating Profit (EBIT)",
                "fa-cogs",
                data.totals.operating_profit,
                data.compare_totals?.operating_profit,
                true,
              )}
              {stat(
                "Net Profit After Tax",
                "fa-trophy",
                data.totals.net_profit_after_tax,
                data.compare_totals?.net_profit_after_tax,
                true,
              )}
            </div>
          )}

          {unclassified && unclassified.children.length > 0 && (
            <div className="coa-notice is-alert">
              <i className="fa fa-exclamation-triangle"></i>
              <span>
                {unclassified.children.length} income or expense{" "}
                {unclassified.children.length === 1 ? "account is" : "accounts are"}{" "}
                not mapped to any line of this statement. Their net{" "}
                <strong>{amount(unclassified.amount)}</strong> is shown under
                Unclassified Income / (Expenses). Add them to the account
                mapping at the top of <code>profitAndLoss()</code> in
                ReportChartOfAccountController.
              </span>
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-file-text-o me-2"></i>
                {TITLE}
                <span className="coa-head-note mg-l-10">Amounts in Taka</span>
              </span>
              {data && (
                <span className="d-flex align-items-center gap-3 coa-no-print">
                  <label className="fs-switch">
                    <input
                      type="checkbox"
                      checked={hideZero}
                      onChange={(e) => setHideZero(e.target.checked)}
                    />
                    Hide zero balances
                  </label>
                  {allKeys.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={toggleAll}
                    >
                      <i
                        className={`fa fa-${allExpanded ? "compress" : "expand"} mg-r-6`}
                      ></i>
                      {allExpanded ? "Collapse all" : "Expand all"}
                    </button>
                  )}
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The statement could not be loaded.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="fs-table">
                  <thead>
                    <tr>
                      <th>Particulars</th>
                      <th>Code</th>
                      <th className="coa-num">
                        {periodLabel(data.from, data.to)}
                      </th>
                      <th className="coa-num">% of Revenue</th>
                      {hasCompare && (
                        <th className="coa-num">
                          {periodLabel(data.compare_from, data.compare_to)}
                        </th>
                      )}
                      {hasCompare && <th className="coa-num">% of Revenue</th>}
                      {hasCompare && <th className="coa-num">Change</th>}
                    </tr>
                  </thead>
                  <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                    {rows.map(renderRow)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitAndLoss;
